import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Form, Input, Select } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { GetPaymentGatewayApi } from "../../../../api/request/wallet/deposit";
import UpiDeposit from "./components/UpiDeposit";
import UsdtTrc20 from "./components/UsdtTrc20";
import Stripe from "./components/Stripe";
import CreditDebitCard from "./components/CreditDebitCard";
import ZaroPay from "./components/ZaroPay";
import { GetAccountsApi } from "../../../../api/request/transfer";
import DepositRequest from "./DepositRequest";
import CashDepositMobile from "./components/CashDepositMobile.jsx";
import PayPal from "./components/PayPal.jsx";
import ZinaCardPayment from "./components/ZinaCardPayment.jsx";
import Balance from "../../../../assets/Balance.jsx";

const DepositMobile = ({ setPaymentMethods }) => {
  const [form] = Form.useForm();
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [step, setStep] = useState(1);
  const [isRefresh, setIsRefresh] = useState(false);
  const [isCashDeposit, setIsCashDeposit] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("wallet");

  const getPaymentGateway = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetPaymentGatewayApi();
      if (data?.success) setPaymentGateways(data?.data);
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getAccounts = async () => {
    setIsLoading(true);
    try {
      const { data } = await GetAccountsApi(
        new URLSearchParams({ FilterText: "Approved" }).toString()
      );
      if (data?.success) {
        setAccounts(data?.data?.filter((account) => account?.isLive));
      }
    } catch (error) {
      if (error?.response?.data?.error?.message)
        toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPaymentGateway();
    getAccounts();
  }, []);

  // Set default payment method to first option when paymentGateways is loaded
  useEffect(() => {
    if (paymentGateways.length > 0 && !paymentMethod) {
      setPaymentMethod(paymentGateways[0]);
      setPaymentMethods(paymentGateways[0]);
    }
  }, [paymentGateways]);
  const validateAmount = (_, value) => {

    if (!value) {
      return Promise.reject(new Error("Please enter amount."));
    }

    if (Number(value) <= 0) {
      return Promise.reject(new Error("Please enter a valid amount."));
    }

    if (amount < paymentMethod?.minAmount) {
      return Promise.reject(
        new Error(`Amount must be at least $${paymentMethod?.minAmount}.`)
      );
    }

    if (amount > paymentMethod?.maxAmount) {
      return Promise.reject(
        new Error(`Amount cannot exceed $${paymentMethod?.maxAmount}.`)
      );
    }

    return Promise.resolve();
  };

  const handleSubmit = (values) => {
    if ((values?.amount || values?.inrAmount) && paymentMethod) {
      setStep(2);
    }
  };

  const resetAll = () => {
    form.resetFields();
    setAmount(0);
    setPaymentMethod(null);
    setStep(1);
    setIsRefresh(true);
  };

  useEffect(() => {
    if (!paymentMethod) return;

    form.resetFields(["amount", "inrAmount"]);
    setAmount(0);
    setIsRefresh(true);

    if (
      paymentMethod?.name === "Cash Deposit" ||
      paymentMethod?.name === "Bank Transfer"
    ) {
      setIsCashDeposit(true);
    } else {
      setIsCashDeposit(false);
    }

    setStep(1);
  }, [paymentMethod]);

  const renderStep = () => {
    switch (paymentMethod?.name) {
      case "Bank Transfer":
        return <CashDepositMobile amount={amount} resetAll={resetAll} loginTerminalId={selectedAccount} />;
      case "UPI":
        return (
          <UpiDeposit
            amount={amount}
            paymentMethod={paymentMethod}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "USDT-TRC20":
        return (
          <UsdtTrc20
            amount={amount}
            paymentMethod={paymentMethod}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "Debit/Credit Card":
        return (
          <Stripe
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "Network":
        return (
          <CreditDebitCard
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "PayPal":
        return (
          <PayPal
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "Crypto":
        return (
          <ZaroPay
            amount={amount}
            paymentMethod={paymentMethod}
            accounts={accounts}
            resetAll={resetAll}
            loginTerminalId={selectedAccount}
          />
        );
      case "Easypaisa":
        return (
          <ZinaCardPayment
            amount={amount}
            resetAll={resetAll}
            QrCode={paymentMethod?.qrCodeUrl}
            loginTerminalId={selectedAccount}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="mobile-dashboard min-h-screen">
      <div className="mb-4">
        <div className="relative">
          <Form
            form={form}
            layout="vertical"
            className="space-y-5"
            onFinish={handleSubmit}
          >
            <div>
              <label className="text-lg font-medium text-gray-700 block mb-2">Select Payment Method</label>
              <Select
                value={paymentMethod?.name}
                onChange={(value) => {
                  const selected = paymentGateways.find(
                    (pg) => pg.name === value
                  );
                  setPaymentMethod(selected);
                  setPaymentMethods(selected);
                }}
                className="w-full !bg-white rounded-xl"
                size="large"               
                placeholder="Select Payment Method"
                popupClassName="custom-select-dropdown"
                dropdownAlign={{ offset: [0, 0] }}
                dropdownStyle={{
                  borderRadius: "16px",
                  padding: "8px",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
                  border: "none",
                  marginTop: "-10px",
                }}
              >
                {paymentGateways.map((item) => (
                  <Select.Option key={item?.id} value={item?.name}>
                    <div className="flex items-center gap-2 py-1 px-2 rounded-lg">
                      <img
                        src={item?.paymentLogoUrl}
                        alt="Icon"
                        className="h-[30px] w-[30px] object-contain"
                      />
                      <span className="font-semibold text-sm">{item?.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* put where to deposit here */}
            <div className="mb-4">
              <label className="text-lg font-medium text-gray-700 block mb-2">Where to Deposit</label>
              <Select
                size="large"
                placeholder="Select account to deposit"
                className="w-full"
                onChange={(value) => setSelectedAccount(value)}
                value={selectedAccount}
              >
                <Select.Option value="wallet">
                  <div className="flex items-center gap-3 py-1">
                    <Balance color="black" className="size-6" />
                    <div className="flex items-center">
                      <span className="text-base font-medium">Wallet</span>
                      <span className="text-sm text-gray-500 ms-2">$0</span>
                    </div>
                  </div>
                </Select.Option>
                {accounts?.map((item, index) => (
                  <Select.Option key={index} value={item?.terminalID}>
                    <div className="flex items-center gap-3 py-1">
                      <img
                        src="/assets/images/vertex_logo.png"
                        alt="Icon"
                        className="h-6 w-6 object-contain"
                      />
                      <div className="flex items-center">
                        <span className="text-base font-medium">{item?.terminalID}</span>
                        <span className="text-sm text-gray-500 ms-2">${item?.balance || 0}</span>
                      </div>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>


            <>
              <Form.Item
                name="inrAmount"
                label={
                  <span className="text-lg text-gray-500 font-medium">
                    Amount In USD
                  </span>
                }
                rules={[{ validator: validateAmount }]}
              >
                <Input
                  type="number"
                  prefix="$"
                  placeholder="Amount in USD"
                  size="large"
                  onChange={(e) => setAmount(e?.target?.value)}
                />
              </Form.Item>
            </>


            {/* Where to Deposit Dropdown (copied from PaymentDetails) */}

          </Form>
        </div>
      </div>

      {paymentMethod && renderStep()}

      <div className="mt-5">
        <DepositRequest isRefresh={isRefresh} setIsRefresh={setIsRefresh} />
      </div>
    </div>
  );
};

export default DepositMobile;
